import { Global, css } from "@emotion/react";
import {
  Button,
  Font,
  TextLink,
  color,
} from "@moneyforward/mfx_biz_react_components";
import { spModeBreakPoint } from "@/styles/theme";
import companyLogo from "@/assets/logo.png";
import config from "@/libs/config";
import {
  ExternalToolsUsageLink,
  IdLogo,
  PrivacyPolicyLink,
  SpecialTermsLink,
  TermsOfUseLink,
} from "../atoms";

type Props = {};

export const SystemErrorPage: React.FC<Props> = ({}: Props) => {
  return (
    <>
      <Global styles={resetStyle} />
      <div css={systemErrorPageStyle}>
        <header css={headerStyle}>
          <div css={companyLogoStyle}>
            <div css={companyLogoInnerStyle}>
              <img src={companyLogo.src} height={24} alt={config.BANK_NAME} />
            </div>
          </div>
          <div css={verticalRuleContainerStyle}>
            <div css={verticalRuleStyle} />
          </div>
          <div css={mikatanoLogoContainerStyle}>
            <div css={mikatanoLogoContainerInnerStyle}>
              <IdLogo color={config.BANK_COLOR} />
            </div>
          </div>
        </header>
        <main css={contentsContainerStyle}>
          <section>
            <Font component="h1" variant="xlb">
              エラーが発生しました
            </Font>
          </section>
          <section css={messageContainerStyle}>
            <div css={messageSummaryStyle}>
              <Font>
                システムで何らかの障害が発生している可能性があります。 <br />
                <span>復旧までしばらくお待ちいただくか、</span>
                <TextLink
                  href="https://support.id.x.moneyforward.com/hc/ja/articles/16397458948377-%E3%82%A2%E3%82%AF%E3%82%BB%E3%82%B9%E3%81%97%E3%81%A6%E3%82%82%E7%94%BB%E9%9D%A2%E3%81%8C%E7%9C%9F%E3%81%A3%E7%99%BD%E3%81%AB%E3%81%AA%E3%82%8A%E3%81%BE%E3%81%99-%E8%A7%A3%E6%B1%BA%E6%96%B9%E6%B3%95%E3%82%92%E6%95%99%E3%81%88%E3%81%A6%E3%81%8F%E3%81%A0%E3%81%95%E3%81%84"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  よくある質問
                </TextLink>
                <span>に記載の手順をお試しください。</span>
              </Font>
            </div>
            <div css={operationContainerStyle}>
              <Button
                type="button"
                variant="secondary"
                onClick={() => (global.location.href = "/")}
                css={buttonToTopStyle}
              >
                トップへ戻る
              </Button>
            </div>
          </section>
        </main>
        <footer css={footerStyle}>
          <div css={footerContainerStyle}>
            <ul css={termsConntainerStyle}>
              <li>
                <TermsOfUseLink />
              </li>
              <li>
                <PrivacyPolicyLink />
              </li>
              <li>
                <SpecialTermsLink />
              </li>
              <li>
                <ExternalToolsUsageLink />
              </li>
            </ul>
          </div>
        </footer>
      </div>
    </>
  );
};

/**
 * Style definition for resetting the base style. Ideally, it should not be defined limited to specific components,
 * but due to the extensive impact when applied globally, it is defined here as a temporary measure.
 */
const resetStyle = css`
  *,
  ::before,
  ::after {
    box-sizing: border-box;
    border-width: 0px;
    border-style: solid;
  }
  html,
  body {
    height: 100%;
    font-size: 62.5%;
  }
  body,
  ul {
    margin: 0;
    padding: 0;
    li {
      list-style: none;
    }
  }
  div#__next {
    height: 100%;
  }
`;

const systemErrorPageStyle = css`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const headerStyle = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  column-gap: 1.8rem;
  padding: 0.8rem;
  border-bottom: 0.1rem solid ${color.gray300};

  @media (max-width: ${spModeBreakPoint}px) {
    flex-direction: column;
    padding: 0;
  }
`;

const verticalRuleContainerStyle = css`
  height: 2rem;

  @media (max-width: ${spModeBreakPoint}px) {
    display: none;
  }
`;

const companyLogoStyle = css`
  @media (max-width: ${spModeBreakPoint}px) {
    width: 100%;
    text-align: center;
  }
`;

const companyLogoInnerStyle = css`
  @media (max-width: ${spModeBreakPoint}px) {
    padding: 0.8rem;
  }
`;

const verticalRuleStyle = css`
  display: inline-block;
  width: 0.1rem;
  background-color: ${color.black};
  min-height: 2rem;
`;

const mikatanoLogoContainerStyle = css`
  height: 1.6rem;

  @media (max-width: ${spModeBreakPoint}px) {
    height: auto;
    display: flex;
    width: 100%;
    border-top: 0.1rem solid ${color.gray300};
  }
`;

const mikatanoLogoContainerInnerStyle = css`
  @media (max-width: ${spModeBreakPoint}px) {
    padding: 1.2rem 1.6rem;
  }
`;

const contentsContainerStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  row-gap: 3.2rem;
  padding: 5.6rem 4rem 4rem;

  @media (max-width: ${spModeBreakPoint}px) {
    padding: 4rem 1.6rem;
  }
`;

const messageContainerStyle = css`
  display: flex;
  flex-direction: column;
  row-gap: 2.4rem;
  max-width: 64rem;
  padding: 3.2rem;
  border-radius: 0.4rem;
  border: 0.1rem solid ${color.gray300};

  @media (max-width: ${spModeBreakPoint}px) {
    padding: 1.6rem;
  }
`;

const operationContainerStyle = css`
  text-align: center;
`;

const buttonToTopStyle = css`
  font-size: 1.4rem;
`;

const messageSummaryStyle = css`
  text-align: center;
`;

const footerStyle = css`
  flex-grow: 1;

  display: flex;
  flex-direction: column;
  justify-content: flex-end;
`;

const footerContainerStyle = css`
  min-height: 16.8rem;
  padding: 2.4rem;
  background-color: ${color.gray100};
`;

const termsConntainerStyle = css`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  column-gap: 3rem;
  row-gap: 1rem;
  justify-content: center;

  @media (max-width: ${spModeBreakPoint}px) {
    flex-direction: column;
    justify-content: flex-start;
    row-gap: 0.8rem;
  }
`;
